// DashboardMonthly.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CalendarRange,
  RefreshCcw,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  BarChart2,
  Filter,
  ShieldAlert
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apinode from "../../services/apinode";

/** ===================== Utils ===================== **/
const monthLabel = (y, m) => {
  const date = new Date(Date.UTC(y, m - 1, 1));
  return date
    .toLocaleDateString("pt-BR", {
      month: "short",
      year: "2-digit",
      timeZone: "UTC",
    })
    .replace(".", "");
};

const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);

const getAllDistinctMonths = (contas) => {
  const set = new Set();
  (contas || []).forEach((c) =>
    (c.months || []).forEach((m) =>
      set.add(`${m.year}-${String(m.month).padStart(2, "0")}`)
    )
  );
  return [...set]
    .map((k) => {
      const [y, mm] = k.split("-");
      return { year: Number(y), month: Number(mm) };
    })
    .sort((a, b) =>
      a.year === b.year ? a.month - b.month : a.year - b.year
    );
};

const computeTotalsForMonths = (months, selected) => {
  if (!months || months.length === 0)
    return { opened: 0, closed: 0, total: 0, affected: 0 };

  if (!selected || selected === "all") {
    return {
      opened: sum(months.map((m) => m.opened)),
      closed: sum(months.map((m) => m.closed)),
      total: sum(months.map((m) => m.total)),
      affected: sum(months.map((m) => m.affected)),
    };
  }

  const item = months.find(
    (m) => m.year === selected.year && m.month === selected.month
  );

  return {
    opened: item?.opened || 0,
    closed: item?.closed || 0,
    total: item?.total || 0,
    affected: item?.affected || 0,
  };
};

// escolhe mês inicial: atual se existir; senão, o mais recente
const pickInitialMonth = (monthsDistinct) => {
  if (!monthsDistinct?.length) return "all";
  const now = new Date();
  const cy = now.getUTCFullYear();
  const cm = now.getUTCMonth() + 1; // 1-12
  const hasCurrent = monthsDistinct.find(
    (m) => m.year === cy && m.month === cm
  );
  if (hasCurrent) return { year: cy, month: cm };
  return monthsDistinct[monthsDistinct.length - 1];
};

/** ===================== CSS ===================== **/
/** ===================== CSS ===================== **/
const css = `
.dm-root{position:relative;width:100%;background:#ffffff;color:#0f172a;font-family:ui-sans-serif,system-ui}
.dm-card{background:#fff;border:1px solid #e5e7eb;border-radius:14px;box-shadow:0 6px 18px rgba(0,0,0,.06);overflow:hidden}
.dm-header{display:flex;align-items:center;justify-content:space-between;padding:14px 16px;border-bottom:1px solid #eef2f7}
.dm-title{display:flex;align-items:center;gap:10px;font-weight:700}
.dm-actions{display:flex;gap:8px}
.dm-btn{display:inline-flex;align-items:center;gap:8px;padding:8px 12px;border-radius:10px;border:1px solid #e5e7eb;background:#f8fafc;font-weight:600;cursor:pointer}
.dm-btn:hover:not(:disabled){background:#f1f5f9}
.dm-body{padding:14px;display:flex;flex-direction:column;gap:16px}
.dm-filter{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.dm-pills{display:flex;gap:8px;flex-wrap:wrap}
.dm-pill{padding:6px 10px;border-radius:999px;font-size:12px;cursor:pointer;border:1px solid #e5e7eb;background:#fff;color:#111827;font-weight:600}
.dm-pill.active{background:#0ea5e9;color:#111827;border-color:#0ea5e9;font-weight:700}
.dm-sub{font-size:12px;color:#64748b}
.dm-kpis{display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px}
.dm-kpi{border:1px solid #e8edf3;border-radius:12px;padding:12px;background:#fff;display:flex;align-items:center;gap:12px}
.dm-kpiIcon{width:36px;height:36px;display:grid;place-items:center;border-radius:10px;background:#f8fafc;border:1px solid #e5e7eb}
.dm-kpiContent{display:flex;flex-direction:column}
.dm-kpiLabel{font-size:12px;color:#64748b}
.dm-kpiValue{font-size:18px;font-weight:800}
.dm-accounts{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:12px}
.dm-account{border:1px solid #e8edf3;border-radius:12px;padding:12px;background:#fbfdff;display:flex;flex-direction:column;gap:10px;cursor:pointer;transition:all 0.2s}
.dm-account:hover{background:#f0f9ff}
.dm-accountHead{display:flex;align-items:center;justify-content:space-between}
.dm-badge{background:#0ea5e9;color:#fff;padding:6px 10px;border-radius:999px;font-size:12px;font-weight:700}
.dm-user{font-size:12px;color:#64748b}
.dm-row{display:flex;flex-wrap:wrap;gap:8px}
.dm-chip{display:inline-flex;align-items:center;gap:6px;background:#f8fafc;border:1px solid #e5e7eb;padding:6px 10px;border-radius:999px;font-size:12px}
.dm-empty{padding:24px;text-align:center;color:#64748b;display:flex;gap:8px;align-items:center;justify-content:center}
`;


/** ===================== Component ===================== **/
export default function DashboardMonthly({ initialData = null }) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const abortRef = useRef(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      abortRef.current?.abort?.();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const contaId = localStorage.getItem("idconta") || "514066249";
      const res = await apinode.get(`/summary-monthly/${contaId}`, {
        signal: ctrl.signal,
        timeout: 12000,
      });
      const payload = res.data;
      setData(payload);
      const monthsDistinct = getAllDistinctMonths(payload?.contas || []);
      setSelectedMonth(pickInitialMonth(monthsDistinct));
    } catch (e) {
      if (e?.name !== "CanceledError" && e?.code !== "ERR_CANCELED") {
        setError(e?.message || "Falha ao carregar");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData) {
      fetchData();
    } else {
      setData(initialData);
      setLoading(false);
      const monthsDistinct = getAllDistinctMonths(initialData?.contas || []);
      setSelectedMonth(pickInitialMonth(monthsDistinct));
    }
    return () => abortRef.current?.abort?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  const distinctMonths = useMemo(
    () => getAllDistinctMonths(data?.contas || []),
    [data]
  );

  const overall = useMemo(() => {
    if (!data?.contas) return { opened: 0, closed: 0, total: 0, affected: 0 };
    const allMonths = data.contas.flatMap((c) => c.months || []);
    return computeTotalsForMonths(allMonths, selectedMonth);
  }, [data, selectedMonth]);

  return (
    <>
      <style>{css}</style>
      <div className="dm-root">
        <div className="dm-card">
          <div className="dm-header">
            <div className="dm-title">
              <CalendarRange size={18} />
              <span>Resumo mensal (reclamações) — Contas</span>
            </div>
            <div className="dm-actions">
              <button className="dm-btn" onClick={fetchData} disabled={loading}>
                <RefreshCcw size={16} />
                {loading ? "Atualizando..." : "Atualizar"}
              </button>
            </div>
          </div>

          <div className="dm-body">
            {error && (
              <div className="dm-empty">
                <AlertTriangle size={16} /> {String(error)}
              </div>
            )}

            {/* Filtro de meses */}
            <section className="dm-filter">
              <span className="dm-sub">
                <Filter size={14} /> Mês:
              </span>
              <div className="dm-pills">
                <button
                  className={`dm-pill ${
                    selectedMonth === "all" ? "active" : ""
                  }`}
                  onClick={() => setSelectedMonth("all")}
                >
                  Todos
                </button>
                {distinctMonths.map((m, i) => {
                  const active =
                    typeof selectedMonth === "object" &&
                    selectedMonth?.year === m.year &&
                    selectedMonth?.month === m.month;
                  return (
                    <button
                      key={`${m.year}-${m.month}-${i}`}
                      className={`dm-pill ${active ? "active" : ""}`}
                      onClick={() =>
                        setSelectedMonth({ year: m.year, month: m.month })
                      }
                      title={`${m.year}-${String(m.month).padStart(2, "0")}`}
                    >
                      {monthLabel(m.year, m.month)}
                    </button>
                  );
                })}
              </div>
            </section>

            {/* KPIs gerais */}
            <section className="dm-kpis">
              <div className="dm-kpi">
                <div className="dm-kpiIcon">
                  <TrendingUp size={18} />
                </div>
                <div className="dm-kpiContent">
                  <span className="dm-kpiLabel">Abertos (geral)</span>
                  <span className="dm-kpiValue">{overall.opened}</span>
                </div>
              </div>

              <div className="dm-kpi">
                <div className="dm-kpiIcon">
                  <CheckCircle2 size={18} />
                </div>
                <div className="dm-kpiContent">
                  <span className="dm-kpiLabel">Fechados (geral)</span>
                  <span className="dm-kpiValue">{overall.closed}</span>
                </div>
              </div>

              <div className="dm-kpi">
                <div className="dm-kpiIcon">
                  <BarChart2 size={18} />
                </div>
                <div className="dm-kpiContent">
                  <span className="dm-kpiLabel">Total (geral)</span>
                  <span className="dm-kpiValue">{overall.total}</span>
                </div>
              </div>

              <div className="dm-kpi">
                <div className="dm-kpiIcon">
                  <ShieldAlert size={18} />
                </div>
                <div className="dm-kpiContent">
                  <span className="dm-kpiLabel">Afetadas (geral)</span>
                  <span className="dm-kpiValue">
                    {overall.affected} de {overall.total}
                  </span>
                </div>
              </div>
            </section>

            {/* Cards por conta */}
            <section className="dm-accounts">
              {(data?.contas || []).map((c) => {
                const totals = computeTotalsForMonths(
                  c.months || [],
                  selectedMonth
                );
                return (
                  <article
                    key={c.user_id}
                    className="dm-account"
                    onClick={() => navigate(`/claims?conta=${c.user_id}`)}
                  >
                    <div className="dm-accountHead">
                      <span className="dm-badge">{c.apelido || "Conta"}</span>
                      <span className="dm-user">user_id: {c.user_id}</span>
                    </div>

                    <div className="dm-row">
                      <span className="dm-chip">
                        <TrendingUp size={14} /> Abertos:{" "}
                        <strong>{totals.opened}</strong>
                      </span>
                      <span className="dm-chip">
                        <CheckCircle2 size={14} /> Fechados:{" "}
                        <strong>{totals.closed}</strong>
                      </span>
                      <span className="dm-chip">
                        <BarChart2 size={14} /> Total:{" "}
                        <strong>{totals.total}</strong>
                      </span>
                      <span className="dm-chip">
                        <ShieldAlert size={14} /> Afetadas:{" "}
                        <strong>
                          {totals.affected} de {totals.total}
                        </strong>
                      </span>
                    </div>
                  </article>
                );
              })}
            </section>

            {(!data?.contas || data.contas.length === 0) &&
              !loading &&
              !error && (
                <div className="dm-empty">Nenhuma conta para exibir.</div>
              )}
          </div>
        </div>
      </div>
    </>
  );
}
